import React from 'react';
import {
  View, Pressable, StyleSheet, ScrollView, Platform, useWindowDimensions,
} from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { WORK_SUGGESTIONS, pickWorkSuggestionItems, resolveWorkSuggestionPrompt } from '@/constants/work';
import useWorkStarterPrompts from '@/hooks/useWorkStarterPrompts';

export default function WorkSuggestionChips({
  onSelect,
  compact = false,
  expanded = false,
  carousel = false,
  hasFiles = false,
  collectionId = null,
  collectionTitle = null,
  enabled = true,
}) {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const border = colors.border_color_secondary;
  // Keep a deliberate peek of the next workflow at narrow widths. It makes the
  // horizontal rail discoverable without adding another instruction to read.
  const carouselCardWidth = Math.min(286, Math.max(240, width - 88));
  const personalized = useWorkStarterPrompts({ collectionId, collectionTitle, enabled });
  const suggestions = pickWorkSuggestionItems(personalized);
  const isPersonalized = suggestions !== WORK_SUGGESTIONS;

  const chips = suggestions.map((item) => (
    <Pressable
      key={item.id}
      onPress={() => onSelect?.(resolveWorkSuggestionPrompt(item, { hasFiles }))}
      accessibilityRole="button"
      accessibilityLabel={item.label}
      accessibilityHint="Adds this workflow to the Work composer"
      style={({ hovered, pressed }) => [
        styles.chip,
        expanded && styles.card,
        carousel && styles.carouselCard,
        carousel && { width: carouselCardWidth, flexBasis: carouselCardWidth },
        {
          borderColor: border,
          backgroundColor: hovered || pressed
            ? colors.solid_color_secondary
            : colors.solid_color_primary,
        },
        compact && styles.chipCompact,
      ]}
    >
          <View style={[expanded && styles.cardIcon, expanded && { backgroundColor: colors.solid_color_secondary }]}>
            <Icon name={item.icon || 'file-lines'} size={expanded ? 15 : 13} color={colors.text_color_secondary} />
          </View>
          <View style={expanded ? styles.cardCopy : undefined}>
            <Text size="s" weight={expanded ? 'semibold' : 'medium'} numberOfLines={1}>{item.label}</Text>
        {expanded && (
          <Text size="xxs" color="secondary" numberOfLines={2} style={styles.description}>
            {item.description}
          </Text>
        )}
      </View>
      {expanded && <Icon name="arrow-right" size={11} color={colors.text_color_tertiary} />}
    </Pressable>
  ));

  const accessibilityLabel = isPersonalized
    ? 'Suggested Work jobs based on your memory'
    : expanded ? 'Proven Work workflows' : 'Work suggestions';

  // Compact chips on the new-chat Work composer wrap like Chat-mode pills.
  // The expanded coworker landing still uses a mobile carousel of cards.
  if (carousel) {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        accessibilityLabel={accessibilityLabel}
        snapToInterval={carouselCardWidth + 8}
        snapToAlignment="start"
        decelerationRate="fast"
        contentContainerStyle={[styles.row, styles.carouselRow]}
      >
        {chips}
      </ScrollView>
    );
  }

  return (
    <View
      style={[styles.row, expanded ? styles.grid : styles.wrap]}
      accessibilityLabel={accessibilityLabel}
    >
      {chips}
    </View>
  );
}

WorkSuggestionChips.propTypes = {
  onSelect: PropTypes.func,
  compact: PropTypes.bool,
  expanded: PropTypes.bool,
  carousel: PropTypes.bool,
  hasFiles: PropTypes.bool,
  collectionId: PropTypes.string,
  collectionTitle: PropTypes.string,
  enabled: PropTypes.bool,
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 8,
    paddingVertical: 2,
  },
  wrap: {
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
  },
  grid: {
    flexWrap: 'wrap',
    width: '100%',
  },
  carouselRow: {
    paddingRight: 16,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  chipCompact: {
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  card: {
    flexBasis: 220,
    flexGrow: 1,
    minHeight: 76,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 11,
  },
  carouselCard: {
    flexGrow: 0,
    flexShrink: 0,
  },
  cardIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardCopy: {
    flex: 1,
  },
  description: {
    marginTop: 2,
    lineHeight: 16,
  },
});
